import { CanvasRenderingTarget2D } from 'fancy-canvas';
import {
  AutoscaleInfo,
  Coordinate,
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  Logical,
  SeriesOptionsMap,
  SeriesType,
  Time
} from 'lightweight-charts';

class RectanglePaneRenderer implements IPrimitivePaneRenderer {
  _topLeft: ViewPoint;
  _bottomRight: ViewPoint;
  _options: RectangleOptions;

  constructor(
    topLeft: ViewPoint,
    bottomRight: ViewPoint,
    options: RectangleOptions
  ) {
    this._topLeft = topLeft;
    this._bottomRight = bottomRight;
    this._options = options;
  }

  draw(target: CanvasRenderingTarget2D) {
    target.useBitmapCoordinateSpace((scope: any) => {
      if (
        this._topLeft.x === null ||
        this._topLeft.y === null ||
        this._bottomRight.x === null ||
        this._bottomRight.y === null
      )
        return;

      const ctx = scope.context;
      const x1 = Math.round(this._topLeft.x * scope.horizontalPixelRatio);
      const y1 = Math.round(this._topLeft.y * scope.verticalPixelRatio);
      const x2 = Math.round(this._bottomRight.x * scope.horizontalPixelRatio);
      const y2 = Math.round(this._bottomRight.y * scope.verticalPixelRatio);

      // Draw rectangle
      ctx.fillStyle = this._options.fillColor;
      ctx.strokeStyle = this._options.borderColor;
      ctx.lineWidth = this._options.borderWidth;

      const width = x2 - x1;
      const height = y2 - y1;

      ctx.beginPath();
      ctx.rect(x1, y1, width, height);

      if (this._options.fill) {
        ctx.fill();
      }
      if (this._options.border) {
        ctx.stroke();
      }
    });
  }
}

interface ViewPoint {
  x: Coordinate | null;
  y: Coordinate | null;
}

class RectanglePaneView implements IPrimitivePaneView {
  _source: Rectangle;
  _topLeft: ViewPoint = { x: null, y: null };
  _bottomRight: ViewPoint = { x: null, y: null };

  constructor(source: Rectangle) {
    this._source = source;
  }

  update() {
    const series = this._source._series;
    const timeScale = this._source._chart.timeScale();

    // Convert time/price to coordinates for both points
    const y1 = series.priceToCoordinate(this._source._topLeft.price);
    const y2 = series.priceToCoordinate(this._source._bottomRight.price);
    const x1 = timeScale.timeToCoordinate(this._source._topLeft.time);
    const x2 = timeScale.timeToCoordinate(this._source._bottomRight.time);

    this._topLeft = { x: x1, y: y1 };
    this._bottomRight = { x: x2, y: y2 };
  }

  renderer() {
    return new RectanglePaneRenderer(
      this._topLeft,
      this._bottomRight,
      this._source._options
    );
  }
}

interface CornerPoint {
  time: Time;
  price: number;
}

export interface RectangleOptions {
  fill: boolean;
  fillColor: string;
  border: boolean;
  borderColor: string;
  borderWidth: number;
}

const defaultOptions: RectangleOptions = {
  fill: true,
  fillColor: 'rgba(0, 100, 255, 0.2)',
  border: true,
  borderColor: 'rgb(0, 100, 255)',
  borderWidth: 1
};

export class Rectangle implements ISeriesPrimitive<Time> {
  _chart: IChartApi;
  _series: ISeriesApi<keyof SeriesOptionsMap>;
  _topLeft: CornerPoint;
  _bottomRight: CornerPoint;
  _paneViews: RectanglePaneView[];
  _options: RectangleOptions;
  _minPrice: number;
  _maxPrice: number;

  constructor(
    chart: IChartApi,
    series: ISeriesApi<SeriesType>,
    topLeft: CornerPoint,
    bottomRight: CornerPoint,
    options?: Partial<RectangleOptions>
  ) {
    this._chart = chart;
    this._series = series;
    this._topLeft = topLeft;
    this._bottomRight = bottomRight;
    this._minPrice = Math.min(this._topLeft.price, this._bottomRight.price);
    this._maxPrice = Math.max(this._topLeft.price, this._bottomRight.price);
    this._options = {
      ...defaultOptions,
      ...options
    };
    this._paneViews = [new RectanglePaneView(this)];
  }

  autoscaleInfo(
    startTimePoint: Logical,
    endTimePoint: Logical
  ): AutoscaleInfo | null {
    const p1Index = this._pointIndex(this._topLeft);
    const p2Index = this._pointIndex(this._bottomRight);
    if (p1Index === null || p2Index === null) return null;

    // Check if the visible time range intersects with the rectangle
    if (endTimePoint < p1Index || startTimePoint > p2Index) return null;

    return {
      priceRange: {
        minValue: this._minPrice,
        maxValue: this._maxPrice
      }
    };
  }

  updateAllViews() {
    this._paneViews.forEach((pw) => pw.update());
  }

  paneViews() {
    return this._paneViews;
  }

  _pointIndex(p: CornerPoint): number | null {
    const coordinate = this._chart.timeScale().timeToCoordinate(p.time);
    if (coordinate === null) return null;
    return this._chart.timeScale().coordinateToLogical(coordinate);
  }
}
